import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Loader2, Send, ChevronLeft, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { TrafficBadge } from "@/components/TrafficBadge";
import { DeltaChip } from "@/components/DeltaChip";
import { cn } from "@/lib/utils";
import { sanitizeError } from "@/lib/errorUtils";

interface KPI {
  id: string;
  name: string;
  unit: string;
  min_band: number | null;
  max_band: number | null;
}

interface ExistingValue {
  kpi_id: string;
  value: number;
  id: string;
}

interface PreviousValue {
  kpi_id: string;
  value: number;
}

const getWeekStart = (offset = 0): string => {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1) + offset * 7;
  const monday = new Date(now);
  monday.setDate(diff);
  return monday.toISOString().split("T")[0];
};

const formatWeekLabel = (dateStr: string): string => {
  const d = new Date(dateStr);
  const end = new Date(d);
  end.setDate(end.getDate() + 6);
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })} — ${end.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
};

const QuickSubmit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState("");
  const [kpis, setKPIs] = useState<KPI[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [existing, setExisting] = useState<Record<string, ExistingValue>>({});
  const [previous, setPrevious] = useState<Record<string, PreviousValue>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);

  const weekStart = getWeekStart(weekOffset);

  useEffect(() => {
    if (id) loadData();
  }, [id, weekOffset]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [companyRes, kpisRes] = await Promise.all([
        supabase.from("companies").select("name").eq("id", id).single(),
        supabase.from("kpis").select("id, name, unit, min_band, max_band").eq("company_id", id).order("sort_order"),
      ]);

      if (companyRes.error) throw companyRes.error;
      if (kpisRes.error) throw kpisRes.error;

      setCompanyName(companyRes.data.name);
      setKPIs(kpisRes.data || []);

      // Load existing values for this week and previous week
      const kpiIds = (kpisRes.data || []).map((k) => k.id);
      if (kpiIds.length > 0) {
        const prevWeek = getWeekStart(weekOffset - 1);
        const [existingRes, prevRes] = await Promise.all([
          supabase.from("kpi_values").select("id, kpi_id, value").in("kpi_id", kpiIds).eq("week_start_date", weekStart),
          supabase.from("kpi_values").select("kpi_id, value").in("kpi_id", kpiIds).eq("week_start_date", prevWeek),
        ]);

        const existingMap: Record<string, ExistingValue> = {};
        const valuesMap: Record<string, string> = {};
        for (const v of existingRes.data || []) {
          existingMap[v.kpi_id] = v;
          valuesMap[v.kpi_id] = String(v.value);
        }
        setExisting(existingMap);
        setValues(valuesMap);

        const prevMap: Record<string, PreviousValue> = {};
        for (const v of prevRes.data || []) {
          prevMap[v.kpi_id] = v;
        }
        setPrevious(prevMap);
      }
    } catch (error) {
      toast.error(sanitizeError(error, "Failed to load KPIs"));
      navigate(`/app/companies/${id}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatus = (kpi: KPI, value: number | null): "green" | "amber" | "red" | "neutral" => {
    if (value === null || value === undefined) return "neutral";
    if (kpi.min_band && value < kpi.min_band) return "red";
    if (kpi.max_band && value > kpi.max_band) return "red";
    const prev = previous[kpi.id];
    if (prev) {
      const change = Math.abs((value - prev.value) / prev.value);
      if (change > 0.2) return "amber";
    }
    return "green";
  };

  const filledCount = Object.values(values).filter((v) => v.trim() !== "").length;
  const allFilled = filledCount === kpis.length && kpis.length > 0;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const toUpsert = kpis
        .filter((kpi) => values[kpi.id]?.trim())
        .map((kpi) => ({
          kpi_id: kpi.id,
          value: parseFloat(values[kpi.id]),
          week_start_date: weekStart,
          submitted_by: user.id,
        }));

      if (toUpsert.length === 0) {
        toast.error("Enter at least one value");
        setSubmitting(false);
        return;
      }

      // Delete existing then insert (upsert pattern)
      const existingIds = toUpsert
        .map((u) => existing[u.kpi_id]?.id)
        .filter(Boolean) as string[];

      if (existingIds.length > 0) {
        await supabase.from("kpi_values").delete().in("id", existingIds);
      }

      const { error } = await supabase.from("kpi_values").insert(toUpsert);
      if (error) throw error;

      setSubmitted(true);
      toast.success(`${toUpsert.length} KPIs submitted! 🎉`);
      setTimeout(() => navigate(`/app/companies/${id}`), 1500);
    } catch (error) {
      toast.error(sanitizeError(error, "Failed to submit"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppShell title="Quick Submit" showBack backTo={`/app/companies/${id}`}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Quick Submit" subtitle={companyName} showBack backTo={`/app/companies/${id}`}>
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-2xl">
        {/* Week Navigator */}
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" size="icon" className="rounded-xl" onClick={() => setWeekOffset((o) => o - 1)}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="text-center">
            <p className="text-sm font-medium">{formatWeekLabel(weekStart)}</p>
            <p className="text-xs text-muted-foreground">
              {weekOffset === 0 ? "This week" : weekOffset === -1 ? "Last week" : `${Math.abs(weekOffset)} weeks ${weekOffset < 0 ? "ago" : "ahead"}`}
            </p>
          </div>
          <Button variant="ghost" size="icon" className="rounded-xl" onClick={() => setWeekOffset((o) => Math.min(o + 1, 0))} disabled={weekOffset >= 0}>
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-between mb-4 text-sm text-muted-foreground">
          <span>{filledCount} of {kpis.length} filled</span>
          <div className="h-2 flex-1 mx-4 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-300 rounded-full"
              style={{ width: `${kpis.length > 0 ? (filledCount / kpis.length) * 100 : 0}%` }}
            />
          </div>
        </div>

        {/* KPI Cards */}
        <div className="space-y-3">
          {kpis.map((kpi, index) => {
            const numVal = values[kpi.id]?.trim() ? parseFloat(values[kpi.id]) : null;
            const status = getStatus(kpi, numVal);
            const prev = previous[kpi.id];
            const delta = numVal !== null && prev ? numVal - prev.value : null;
            const deltaPercent = delta !== null && prev?.value ? (delta / prev.value) * 100 : null;

            return (
              <Card
                key={kpi.id}
                className={cn(
                  "rounded-2xl border-border/50 transition-all duration-200",
                  values[kpi.id]?.trim() && "border-primary/20 shadow-sm"
                )}
                style={{ animationDelay: `${index * 30}ms` }}
              >
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <TrafficBadge status={status} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{kpi.name}</p>
                      {prev && (
                        <p className="text-xs text-muted-foreground">
                          Last: {prev.value}{kpi.unit}
                        </p>
                      )}
                    </div>
                    {deltaPercent !== null && (
                      <DeltaChip value={deltaPercent} unit="%" />
                    )}
                  </div>

                  <div className="relative">
                    <Input
                      type="number"
                      step="any"
                      placeholder={prev ? `Last: ${prev.value}` : "Enter value"}
                      value={values[kpi.id] || ""}
                      onChange={(e) => setValues((v) => ({ ...v, [kpi.id]: e.target.value }))}
                      className="h-12 rounded-xl text-lg font-semibold tabular-nums pr-12"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">
                      {kpi.unit}
                    </span>
                  </div>

                  {kpi.min_band && kpi.max_band && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Target: {kpi.min_band}–{kpi.max_band}{kpi.unit}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Submit Button */}
        <div className="sticky bottom-20 sm:bottom-6 mt-6">
          <Button
            onClick={handleSubmit}
            disabled={submitting || submitted || filledCount === 0}
            className={cn(
              "w-full h-14 rounded-2xl text-base font-semibold shadow-lg transition-all",
              submitted
                ? "bg-success hover:bg-success"
                : "bg-gradient-accent hover:shadow-xl"
            )}
          >
            {submitted ? (
              <>
                <Check className="h-5 w-5 mr-2" />
                Submitted!
              </>
            ) : submitting ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                Submit {filledCount} KPI{filledCount !== 1 ? "s" : ""}
              </>
            )}
          </Button>
        </div>
      </div>
    </AppShell>
  );
};

export default QuickSubmit;
